import { BarcodeDetector as PonyfillBarcodeDetector, prepareZXingModule } from 'barcode-detector/ponyfill';
import type { BarcodeDetectorOptions, DetectedBarcode } from 'barcode-detector/ponyfill';
// The ponyfill's zxing build fetches its .wasm from a jsDelivr CDN by default. A warehouse phone
// on a bad signal, or an installed PWA offline, must not depend on that: ship the binary in our
// own bundle and point `locateFile` at it. The file is precached by the service worker because
// vite-plugin-pwa's globPatterns already covers `wasm`.
import zxingWasmUrl from 'zxing-wasm/reader/zxing_reader.wasm?url';

/** QR is what our stickers carry; Code 128 is what a few third-party cartons carry. */
const FORMATS = ['qr_code', 'code_128'] as const;
const TICK_MS = 120;

interface DetectorLike {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>;
}
type DetectorCtor = new (options?: BarcodeDetectorOptions) => DetectorLike;

let zxingPrepared = false;

/** The native detector when the browser has one (faster, no download), else the ponyfill. */
function detectorClass(): DetectorCtor {
  if ('BarcodeDetector' in globalThis) return (globalThis as unknown as { BarcodeDetector: DetectorCtor }).BarcodeDetector;
  if (!zxingPrepared) {
    zxingPrepared = true;
    prepareZXingModule({
      overrides: {
        locateFile: (path: string, prefix: string) => (path.endsWith('.wasm') ? zxingWasmUrl : prefix + path),
      },
    });
  }
  return PonyfillBarcodeDetector as unknown as DetectorCtor;
}

/** The camera API is missing before we even ask for a stream. */
export class CameraUnavailable extends Error {
  constructor(readonly kind: 'insecure' | 'unsupported') {
    super(kind);
    this.name = 'CameraUnavailable';
  }
}

/**
 * The stream opened but the code reader itself is dead, so no frame will ever decode.
 *
 * The ponyfill fires its wasm load from its own constructor and swallows the failure
 * (`prepareZXingModule({ fireImmediately: true }).catch(() => {})`), so a .wasm that 404s, is
 * blocked by CSP or fails to instantiate never throws where the camera is started. It only shows
 * up later, as `DOMException('Barcode detection service unavailable.', 'NotSupportedError')` from
 * every single `detect()`. Treated as just another unreadable frame, that leaves the worker a live
 * viewfinder and "Point at the sticker" forever — a screen that lies about what it can do, which
 * is worse than one that fails. So it is promoted to a camera failure.
 */
export class DetectorUnavailable extends Error {
  constructor(readonly cause?: unknown) {
    super('detector');
    this.name = 'DetectorUnavailable';
  }
}

/** A rejection from `detect()` that means the reader is gone, not that the frame was blurry. */
const isDetectorDead = (err: unknown): boolean => (err as { name?: string } | null | undefined)?.name === 'NotSupportedError';

export function cameraUnavailable(): CameraUnavailable | null {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    // Browsers hide mediaDevices entirely on a plain-http origin, so an untrusted page and a
    // browser without the API look identical apart from `isSecureContext`.
    const insecure = typeof globalThis.isSecureContext === 'boolean' && !globalThis.isSecureContext;
    return new CameraUnavailable(insecure ? 'insecure' : 'unsupported');
  }
  return null;
}

export const CAMERA_BLOCKED = 'Camera blocked. Allow camera access, or type the code.';
export const CAMERA_NONE = 'No camera found. Type the code instead.';
export const CAMERA_NO_READER = "This phone can't read codes from the camera. Type the code instead.";

/** Every camera failure ends on a sentence a worker can act on, and never on a dead screen. */
export function cameraErrorMessage(err: unknown): string {
  if (err instanceof CameraUnavailable) {
    return err.kind === 'insecure' ? 'Camera needs a secure (https) page. Type the code instead.' : 'This browser cannot use the camera. Type the code instead.';
  }
  if (err instanceof DetectorUnavailable) return CAMERA_NO_READER;
  const name = err instanceof Error ? err.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') return CAMERA_BLOCKED;
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError') return CAMERA_NONE;
  if (name === 'NotReadableError' || name === 'TrackStartError') return 'The camera is busy. Close other camera apps, or type the code.';
  return 'The camera could not start. Type the code instead.';
}

/**
 * Opens the back camera into `video` and reads a barcode out of it every 120 ms.
 * Resolves with the function that stops detection and releases every track (which is what
 * turns the phone's camera light off). Rejects when the camera cannot be opened at all.
 *
 * `onFatal` is how a failure that only appears *after* the stream is live gets out: the loop has
 * already stopped and the tracks are already released by the time it is called, so the caller only
 * has to show the message. Today that is the dead code reader; see `DetectorUnavailable`.
 */
export async function startCamera(video: HTMLVideoElement, onCode: (raw: string) => void, onFatal: (err: unknown) => void = () => undefined): Promise<() => void> {
  const unavailable = cameraUnavailable();
  if (unavailable) throw unavailable;

  const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } } });
  let stopped = false;
  const release = () => {
    for (const track of stream.getTracks()) track.stop();
    if (video.srcObject === stream) video.srcObject = null;
  };

  try {
    video.srcObject = stream;
    // Autoplay can still be refused (a backgrounded tab, low-power mode); detection just idles
    // until the element has frames, so a refused play is not a reason to fail the whole screen.
    await video.play().catch(() => undefined);

    const detector = new (detectorClass())({ formats: [...FORMATS] });
    let busy = false;
    let timer: ReturnType<typeof setInterval>;

    const stop = () => {
      if (stopped) return;
      stopped = true;
      clearInterval(timer);
      release();
    };

    timer = setInterval(() => {
      if (stopped || busy || video.readyState < 2 || video.videoWidth === 0) return;
      busy = true;
      void detector
        .detect(video)
        .then((found) => {
          for (const barcode of found) if (barcode.rawValue) onCode(barcode.rawValue);
        })
        .catch((err: unknown) => {
          // An unreadable frame is normal — a badly held sticker, glare, motion blur — and the
          // next tick tries again. A dead reader is not: it rejects every frame, forever, with a
          // name we can tell apart. Tear the camera down and hand it up as a camera failure, so
          // the viewfinder collapses and the worker gets "Try again" instead of a viewfinder that
          // will never read anything.
          if (!isDetectorDead(err)) return;
          stop();
          onFatal(new DetectorUnavailable(err));
        })
        .finally(() => {
          busy = false;
        });
    }, TICK_MS);

    return stop;
  } catch (err) {
    stopped = true;
    release();
    throw err;
  }
}
